import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import css from './AlgoliaSearchPage.module.css';

const LISTBOX_ID = 'algolia-listing-category-type-listbox';

/**
 * Category type filter — custom list so the menu width matches the trigger
 * (native <select> expands to the longest option text).
 */
const ListingCategoryTypeFilter = props => {
  const { value, onChange, options, intl, ariaLabel } = props;
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = event => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const allTypesLabel = intl.formatMessage({ id: 'AlgoliaSearchPage.allTypes' });

  // Resolve from the options we were handed, not from a `category.{id}`
  // translation: option values are category slugs now, and formatMessage with
  // a missing key renders the key itself — "category.9f8722b8-…" in the
  // filter chip. Legacy numeric values still fall back to the translation.
  const labelFor = optionId => {
    if (optionId === 'all') return allTypesLabel;
    const match = (options || []).find(o => String(o.id) === String(optionId));
    if (match?.label) return match.label;
    return intl.formatMessage({
      id: `category.${optionId}`,
      defaultMessage: String(optionId),
    });
  };

  const selectedLabel = labelFor(value);

  const selectValue = nextValue => {
    onChange({ target: { value: nextValue } });
    setOpen(false);
  };

  return (
    <div className={css.filterTypeDropdown} ref={rootRef}>
      <button
        type="button"
        className={classNames(css.filterSelect, css.filterTypeDropdownTrigger)}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={LISTBOX_ID}
        onClick={() => setOpen(prev => !prev)}
      >
        <span className={css.filterTypeDropdownLabel}>{selectedLabel}</span>
      </button>
      {open ? (
        <ul className={css.filterTypeDropdownMenu} id={LISTBOX_ID} role="listbox">
          <li role="presentation">
            <button
              type="button"
              role="option"
              aria-selected={value === 'all'}
              className={classNames(
                css.filterTypeDropdownOption,
                value === 'all' && css.filterTypeDropdownOptionActive
              )}
              onClick={() => selectValue('all')}
            >
              {allTypesLabel}
            </button>
          </li>
          {options.map(option => {
            const optionId = String(option.id);
            const optionLabel = labelFor(optionId);
            return (
              <li key={optionId} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={value === optionId}
                  className={classNames(
                    css.filterTypeDropdownOption,
                    option.isParent
                      ? css.filterTypeDropdownOptionParent
                      : css.filterTypeDropdownOptionChild,
                    value === optionId && css.filterTypeDropdownOptionActive
                  )}
                  onClick={() => selectValue(optionId)}
                >
                  {optionLabel}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

ListingCategoryTypeFilter.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
  intl: PropTypes.object.isRequired,
  ariaLabel: PropTypes.string.isRequired,
};

export default ListingCategoryTypeFilter;
